<script lang="ts">
import { defineComponent } from 'vue';
import {mapGetters} from 'vuex';
import equal from 'fast-deep-equal';
import DatePicker from '@/components/ui-elements/DatePicker.vue';
import dayjs from 'dayjs';
import TherapyCard from '@/components/cards/TherapyCard.vue';

const isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
dayjs.extend(isSameOrBefore);

export default defineComponent({
  name: 'SearchSlotsForm',
  components: {TherapyCard, DatePicker},
  created() {
    const date = dayjs();
    this.startDate = date.format('YYYY-MM-DD');
    this.endDate = date.add(1, 'week').format('YYYY-MM-DD');
  },
  data() {
    return {
      location: undefined,
      template: undefined,
      days: [],
      selectedStartTime: '10:30',
      selectedEndTime: '19:30',
      daysSelectedAll: false,
      startDate: undefined,
      endDate: undefined,
      sendingTherapies: [],
    };
  },
  watch: {
    location(newLocation) {
      this.loadTemplates();
      // Store location UUID in store for later use
      this.$store.commit('setCurrentOfficeUuid', newLocation);
      // Reset selected slot when location changes
      this.$store.commit('clearSelectedSlot');
    },
    template() {
      const t = {};

      this.therapies.forEach((therapy) => {
        t[therapy.therapyUUID] = [
            therapy.type,
            therapy.duration,
            [],
        ];
      });

      this.sendingTherapies = t;
    },
    days() {
      this.daysSelectedAll = equal(this.days, this.weekDays);
    },
    daysSelectedAll() {
      if (this.daysSelectedAll) {
        this.days = this.weekDays;
      } else if (!this.daysSelectedAll && equal(this.days, this.weekDays)) {
        this.days = [];
      }
    },
    weekDays() {
      if (!!this.weekDays && this.weekDays.length > 0) {
        this.daysSelectedAll = true;
        this.days = [
          ...this.weekDays,
        ];
      } else {
        this.days = [];
      }
    },
  },
  computed: {
    ...mapGetters(['userLocations', 'templates']),
    weekDays() {
      const startDate = this.$dayjs(this.startDate);
      const endDate = this.$dayjs(this.endDate);
      let currentDate = startDate;
      const weekdays = new Set();

      while (currentDate.isSameOrBefore(endDate)) {
        const thisWeekDay = currentDate.format('ddd');

        if (currentDate.day() >= 0 && currentDate.day() <= 6) {
          weekdays.add(thisWeekDay);
        }
        currentDate = currentDate.add(1, 'day');
      }

      return Array.from(weekdays);
    },
    times() {
      const start = new Date();
      const end = new Date();
      start.setHours(5, 30, 0);
      end.setHours(19, 0, 0);
      const arr = [];

      while (start <= end) {
        start.setMinutes(start.getMinutes() + 30);
        arr.push({
          value: start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
          label: start.toLocaleString('en-US', {hour: '2-digit', minute: '2-digit'}),
        });
      }

      return arr;
    },
    therapies() {
      if (!this.template) { return []; }
      return this.templates.find(({id}) => id === this.template).therapies ?? [];
    },
  },
  methods: {
    loadTemplates() {
      this.$store.dispatch('LOAD_LOCATION_TEMPLATES', { locationUUID: this.location });
      this.$store.commit('resetSlots');
      this.template = undefined;
    },
    async sendSearchQuery() {
      this.$emit('is-loading');

      // Reset selected slot before new search
      this.$store.commit('clearSelectedSlot');

      const payload = {
        location: this.location,
        days: this.days,
        from: this.$dayjs(this.startDate).format('DD-MM-YYYY'),
        till: this.$dayjs(this.endDate).format('DD-MM-YYYY'),
        start: this.selectedStartTime,
        finish: this.selectedEndTime,
        therapies: this.sendingTherapies,
      };

      // Store therapies with durations for booking
      this.$store.commit('setCurrentTherapies', this.sendingTherapies);

      // Store duration from first therapy for appointment booking (backwards compatibility)
      const therapyKeys = Object.keys(this.sendingTherapies);
      if (therapyKeys.length > 0) {
        const firstTherapy = this.sendingTherapies[therapyKeys[0]];
        const duration = firstTherapy[1]; // duration is second element
        this.$store.commit('setCurrentDuration', duration);
      }

      await this.$store.dispatch('SEARCH_SLOTS', payload);
      this.$emit('is-loading');
    },
    updateDoctorsList(data: { therapyUUID: string, doctorsExternalIDs: string[] }) {
      const {therapyUUID, doctorsExternalIDs} = data;

      this.sendingTherapies[therapyUUID][2] = doctorsExternalIDs;
    },
  },
});
</script>

<template>
  <div class="searchSlotsContainer">
    <h2 class="sectionTitle my-2">Appointment Search</h2>
    <p class="sectionSubtitle mb-3">Configure your search parameters to find available appointment slots</p>

    <div class="mb-4">
      <div class="flex w-full flex-col gap-4">
        <div class="">
          <v-select
              v-model="location"
              :items="userLocations"
              item-title="name"
              item-value="uuid"
              density="comfortable"
              label="Location"
              variant="solo"
              class="timeSelectClass"
              :disabled="!userLocations"
          ></v-select>
        </div>
        <div class="">
          <v-select
              v-model="template"
              :items="templates"
              item-title="name"
              item-value="id"
              density="comfortable"
              label="Template"
              variant="solo"
              :disabled="!templates"
              class="timeSelectClass"
              :menu-props="{ contentClass: 'showScroller'}"
          ></v-select>
        </div>
      </div>
    </div>

    <div v-if="therapies.length > 0" class="therapiesSection mt-6 mb-4">
      <span v-for="therapy in therapies" :key="therapy.therapyUUID">
        <TherapyCard
            :therapy="therapy"
            @update-doctors-list="updateDoctorsList"
        />
      </span>
    </div>

    <h3 class="subsectionTitle mt-6 mb-3">Date and Time</h3>
<!--    DATE AND TIME section  -->
    <div class="dateTimeSection">
      <div class="flex justify-between">
        <div class="w-1/2 pr-4">
          <div class="inputGroup mb-3">
            <label class="inputLabel">Start Date:</label>
            <div class="inputWrapper">
              <DatePicker v-if="startDate" :selected-date="startDate" @update-date="(date) => this.startDate = date" format="MM/DD/YYYY"/>
            </div>
          </div>
          <div class="inputGroup mb-3">
            <label class="inputLabel">End Date:</label>
            <div class="inputWrapper">
              <DatePicker v-if="endDate" :selected-date="endDate" @update-date="(date) => this.endDate = date" format="MM/DD/YYYY"/>
            </div>
          </div>
        </div>

        <div class="w-1/2 pl-4">
          <div class="inputGroup mb-3">
            <label class="inputLabel">Start Time:</label>
            <div class="inputWrapper">
              <v-select
                  v-model="selectedStartTime"
                  :items="times"
                  item-title="label"
                  item-value="value"
                  hide-details
                  variant="solo"
                  density="compact"
                  :menu-props="{ contentClass: 'showScroller'}"
                  class="timeSelectClass"
              ></v-select>
            </div>
          </div>
          <div class="inputGroup mb-3">
            <label class="inputLabel">End Time:</label>
            <div class="inputWrapper">
              <v-select
                  v-model="selectedEndTime"
                  :items="times"
                  item-title="label"
                  item-value="value"
                  hide-details
                  variant="solo"
                  density="compact"
                  :menu-props="{ contentClass: 'showScroller'}"
                  class="timeSelectClass"
              ></v-select>
            </div>
          </div>
        </div>
      </div>
    </div>

<!--    Week Days section  -->
    <div class="daysSection my-4">
      <label class="inputLabel mb-2">Days:</label>
      <div class="daysWrapper">
        <v-checkbox
            v-model="daysSelectedAll"
            label="All"
            :value="true"
            hide-details
            density="compact"
            class="dayCheckbox"
        ></v-checkbox>
        <v-checkbox
            v-for="day in weekDays"
            :key="day"
            v-model="days"
            :label="day"
            :value="day"
            hide-details
            density="compact"
            class="dayCheckbox"
        />
      </div>
    </div>

    <div class="actionsSection mt-5 flex gap-4 justify-start">
      <v-btn
        class="searchButton text-white"
        color="primary"
        elevation="2"
        size="large"
        variant="flat"
        @click="sendSearchQuery"
      >
        <v-icon class="mr-1">mdi-magnify</v-icon>
        SEARCH SLOTS
      </v-btn>

      <v-checkbox
          label="Auto-update"
          :value="true"
          hide-details
          disabled
          density="compact"
          class="autoUpdateCheckbox ml-4"
      ></v-checkbox>
    </div>
  </div>
</template>

<style>
.showScroller::-webkit-scrollbar {
  width: 5px;
  display: block;
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
}

.showScroller::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px #efefef;
}

.showScroller::-webkit-scrollbar-thumb {
  background-color: #4E8EB1;
}

.showScroller::-webkit-scrollbar-thumb:hover {
  background-color: #0e84c4;
}

.timeSelectClass {
  border-radius: 4px;
}

.timeSelectClass :deep(.v-text-field) {
  min-height: 36px;
}

.timeSelectClass {
  padding-top: 0 !important;
  height: 36px;
}
</style>

<style scoped>
.searchSlotsContainer {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
}

.sectionTitle {
  font-size: 18px;
  font-weight: 700;
  color: #111111;
}

.sectionSubtitle {
  font-size: 13px;
  color: #666666;
}

.subsectionTitle {
  font-size: 16px;
  font-weight: 700;
  color: #111111;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 20px;
  margin-bottom: 15px;
}

.therapiesSection,
.dateTimeSection {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.inputGroup {
  display: flex;
  flex-direction: column;
}

.inputLabel {
  font-size: 13px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 8px;
  display: block;
}

.inputWrapper {
  flex: 1;
}

.daysSection {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.daysWrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dayCheckbox {
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.dayCheckbox .v-input__control) {
  min-height: auto;
}

.actionsSection {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 15px 0;
}

.searchButton {
  font-weight: 600;
  letter-spacing: 0.5px;
  min-width: 180px;
}

.autoUpdateCheckbox {
  margin: 0 !important;
  padding: 0 !important;
}
</style>
